'use client';

import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import { ComponentType, useMemo } from 'react';
import { clarityDarkTheme, clarityLightTheme } from './clarity';

/**
 * withClarityTheme HOC
 *
 * This Higher-Order Component forces the wrapped component to use Clarity themes,
 * regardless of the current system theme. It creates an isolated theme context
 * for the wrapped component without affecting the rest of the application.
 *
 * Theme Mapping:
 * - light -> clarityLight
 * - dark -> clarityDark
 *
 * @param Component - The component to wrap with Clarity theme
 * @returns Wrapped component with forced Clarity theme
 *
 * @example
 * ```tsx
 * const MyComponentWithClarityTheme = withClarityTheme(MyComponent);
 *
 * // Usage
 * <MyComponentWithClarityTheme />
 * ```
 */
export function withClarityTheme<P extends object>(Component: ComponentType<P>): ComponentType<P> {
  const WrappedComponent = (props: P) => {
    const theme = useTheme();

    // Create Clarity theme based on current theme's mode (light/dark)
    const clarityTheme = useMemo(() => {
      const currentMode = theme.palette.mode;
      const clarityThemeConfig = mapToClarityTheme(currentMode);
      return createTheme(clarityThemeConfig);
    }, [theme.palette.mode]);

    // Wrap component with its own ThemeProvider using Clarity theme
    return (
      <ThemeProvider theme={clarityTheme}>
        <Component {...props} />
      </ThemeProvider>
    );
  };

  // Preserve display name for debugging
  const componentName = Component.displayName || Component.name || 'Component';
  WrappedComponent.displayName = `withClarityTheme(${componentName})`;

  return WrappedComponent;
}

/**
 * Maps standard theme modes to Clarity theme configurations
 *
 * @param mode - Current theme mode ('light' or 'dark')
 * @returns Corresponding Clarity theme configuration
 */
function mapToClarityTheme(mode: string) {
  switch (mode) {
    case 'dark':
    case 'clarityDark':
      return clarityDarkTheme;
    case 'light':
    case 'clarityLight':
    default:
      return clarityLightTheme;
  }
}
